import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, AlertTriangle } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { QuotationCTA } from "@/components/site/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSEO } from "@/hooks/useSEO";
import { findService, serviceSlug, IMAGES } from "@/data/catalog";

const APPLICATIONS = ["Residential homes & apartments", "Commercial & retail spaces", "Industrial facilities & workshops", "New build and refurbishment"];
const MATERIALS = ["Quality-grade materials to specification", "Range of finishes available", "Durable, compliant installations", "Supply-and-install or supply-only"];
const DETAIL_FAQS = [
  { q: "How do I get a quotation for this service?", a: "Submit our quotation form with your requirements and any reference photos or drawings. We will arrange a consultation and site assessment before issuing a detailed, itemised quotation." },
  { q: "Do you provide a warranty?", a: "Workmanship terms are confirmed in your written quotation. We only state warranty details that apply to your specific scope of work." },
  { q: "How long does the work take?", a: "Timelines depend on scope, site conditions and approvals. Your quotation includes an indicative schedule confirmed before works begin." },
];

export default function ServiceDetail() {
  const { serviceSlug: svcSlug } = useParams();
  const match = findService(svcSlug);

  useSEO({
    title: match ? `${match.service} Singapore` : "Service",
    description: match ? `${match.service} by WAAN METAL in Singapore — part of our ${match.division.title.toLowerCase()} services. Request a quotation today.` : "Service",
  });

  if (!match) return <Navigate to="/" replace />;
  const { service, division } = match;

  return (
    <Layout>
      <section className="relative bg-[#0A1128] py-20 overflow-hidden" data-testid="service-detail-hero">
        <div className="absolute inset-0"><img src={division.image} alt="" className="w-full h-full object-cover opacity-20" /><div className="absolute inset-0 bg-[#0A1128]/88" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-5 flex-wrap">
            <Link to="/" className="hover:text-[#D97706]">Home</Link><ChevronRight size={13} />
            <Link to={`/services/${division.slug}`} className="hover:text-[#D97706]">{division.title}</Link><ChevronRight size={13} />
            <span className="text-slate-300">{service}</span>
          </nav>
          <p className="eyebrow mb-3">{division.title}</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight max-w-3xl">{service}</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Service overview</h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Our {service.toLowerCase()} service is delivered by experienced teams as part of our {division.title.toLowerCase()} division.
                We handle planning, supply, installation and finishing to a high standard, coordinating any regulated works with
                qualified professionals where required. Final scope and pricing are confirmed in a detailed quotation after a site assessment.
              </p>
            </div>

            {division.regulated && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-5 flex gap-3 text-sm text-amber-900">
                <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-600" />
                <p>Where this involves structural, electrical, fire-safety or other regulated works, the required approvals apply and works are performed by qualified, licensed professionals.</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Typical applications</h3>
                <ul className="space-y-3">{APPLICATIONS.map((a) => <li key={a} className="flex gap-2.5 text-slate-700"><Check size={18} className="text-[#B87333] shrink-0" />{a}</li>)}</ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Materials & finishes</h3>
                <ul className="space-y-3">{MATERIALS.map((a) => <li key={a} className="flex gap-2.5 text-slate-700"><Check size={18} className="text-[#B87333] shrink-0" />{a}</li>)}</ul>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Example work</h3>
              <div className="grid grid-cols-2 gap-4">
                <img src={division.image} alt={service} className="rounded-xl h-48 w-full object-cover" />
                <img src={division.slug === "metal-works-fabrication" ? IMAGES.gate1 : IMAGES.interior1} alt={service} className="rounded-xl h-48 w-full object-cover" />
              </div>
              <p className="mt-2 text-xs text-slate-400">Representative imagery. Verified project photos are shown on our Projects page.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">FAQ</h3>
              <Accordion type="single" collapsible className="space-y-3">
                {DETAIL_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="border border-slate-200 rounded-xl px-5" data-testid={`service-faq-${i}`}>
                    <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl bg-[#0A1128] p-7 text-white">
              <h3 className="text-xl font-bold">Request a quotation</h3>
              <p className="mt-2 text-sm text-slate-300">Get an itemised quotation for {service.toLowerCase()} tailored to your project.</p>
              <Link to="/request-quotation" data-testid="service-detail-quotation-cta" className="mt-5 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold hover:-translate-y-0.5 transition-all">
                Request a Quotation <ArrowRight size={17} />
              </Link>
              <Link to={`/services/${division.slug}`} className="mt-3 flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
                All {division.title} services
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <QuotationCTA />
    </Layout>
  );
}
